import type { TranslationKey } from './ko'

const en: Record<TranslationKey, string> = {
  // ── StudentLogin ───────────────────────────────────────────
  'login.title': 'Student login',
  'login.classCode.label': 'Class code',
  'login.classCode.placeholder': 'e.g. A7K-92F',
  'login.studentId.label': 'Student ID',
  'login.studentId.placeholder': 'e.g. 24-005',
  // Issue #967 — password login path (school code + student ID + password).
  'login.schoolCode.label': 'School code',
  'login.schoolCode.placeholder': 'e.g. SEOUL-01',
  'login.password.label': 'Password',
  'login.password.placeholder': 'Enter your password',
  // Issue #967 — secondary affordance toggling between the password
  // form and the legacy first-login bootstrap form.
  'login.toggle.toBootstrap': 'First time? Set up your password',
  'login.toggle.toPassword': 'I have a password',
  'login.button': 'Start',
  'login.loading': 'Logging in…',
  // Issue #1104 — generic, no enumeration oracle (0046 discipline): the
  // password path has no class code, so name the three fields it actually
  // uses without revealing which one was wrong.
  'login.error.invalidCredentials':
    'School code, student ID, or password is incorrect',
  'login.welcome': 'Welcome, {id}',
  'login.greeting': 'Welcome back',
  'login.greetingSub': 'Where shall we pick up today?',
  'login.allModules': 'All modules',
  'login.moduleCount': '{n} modules',
  'login.selectModule': 'Choose a module to study:',
  'login.startModule': 'Start',
  'login.startingModule': 'Starting…',
  'login.noModules': 'No modules have been added yet. Please ask your teacher.',
  'login.sessionStartError': 'Failed to start session: ',
  'login.logout': 'Log out',
  // Subtle indicator shown in LessonPicker while a silent
  // /auth/student-me re-fetch is in flight (#517).
  'login.refreshing': 'Refreshing…',

  // ── ADR 0051 Phase 4 — school-search picker / forgot-pw / join ──
  'login.noClassChip': 'No class yet',
  'login.schoolSearch.placeholder': 'Search your school name',
  'login.schoolSearch.loading': 'Searching…',
  'login.schoolSearch.noResults': 'No matching schools',
  // #1122 — distinct from noResults: a failed search request (server/network),
  // not a genuine zero-match. Steers the student to the manual-code fallback.
  'login.schoolSearch.searchUnavailable':
    'School search is temporarily unavailable. Enter your school code directly below.',
  'login.schoolSearch.manualToggle': 'Enter school code instead',
  'login.schoolSearch.searchToggle': 'Search by school name',
  'forgot.link': 'Forgot your password?',
  'forgot.button': 'Send request',
  'forgot.backToLogin': 'Back to sign in',
  'forgot.confirm':
    'Your reset request has been sent. Your teacher or administrator will help you once they see it.',
  'forgot.error': "Couldn't send the request. Please try again in a moment.",
  'join.empty.title': "You're not in a class yet",
  'join.empty.body':
    'Enter the class code your teacher gave you to join a class.',
  'join.code.label': 'Class code',
  'join.code.placeholder': 'e.g. A7K-92F',
  'join.submit': 'Join class',
  'join.submitting': 'Joining…',
  'join.error.notFound':
    "We couldn't find that class code. Check with your teacher.",
  'join.error.generic': "Couldn't join the class. Please try again.",

  // ── LessonPicker (ADR 0040 chunk 6) ────────────────────────
  // The student-side lesson picker carries chunk-6's progress
  // chips + the "Continue where you left off" CTA. Status copy
  // matches the four backend states surfaced by
  // ``services.student_progress``.
  'lessonPicker.continueLabel': 'Continue where you left off',
  'lessonPicker.lessonLabel': 'Lesson {n}',
  'lessonPicker.status.notStarted': 'Not started',
  'lessonPicker.status.inProgress': 'In progress',
  'lessonPicker.status.mastered': 'Mastered',
  'lessonPicker.status.abandoned': 'Needs restart',

  // ── TeacherDashboard ───────────────────────────────────────
  'teacher.title': 'Teacher dashboard',
  'teacher.noOrg.title': 'Awaiting invitation',
  'teacher.noOrg.hint':
    'You need to be invited to your school by your admin (IT staff). Ask your admin to send an invitation to this email address. Once you accept, this page will update automatically.',

  // ── ClassListPage (ADR 0040 chunk 5) ───────────────────────
  // Multi-class landing. 0 classes → empty state with inline
  // ClassCodeBar; 1 class → auto-redirect to its dashboard;
  // N classes → card grid sorted by recent activity.
  'classList.title': 'Your classes',
  'classList.subtitle': 'Pick a class to manage at {school}.',
  'classList.subtitleNoOrg': 'Pick a class to manage.',
  'classList.resume': 'Resume {code}',
  'classList.loading': 'Loading your classes…',
  'classList.error.title': "Couldn't load your classes",
  'classList.error.body':
    'This is usually temporary. Reload the page to try again.',
  'classList.empty.title': 'No classes yet',
  'classList.empty.body':
    'Create your first class and share the code with your students.',
  'classList.card.modules': 'modules',
  'classList.card.students': 'students',
  'classList.card.noModules': 'No modules yet',
  'classList.card.lastModule': 'Last module: {date}',
  'classList.card.created': 'Created: {date}',
  // Phase 3 (#777) redesign keys.
  'classList.eyebrow': '{school}',
  'classList.eyebrowNoOrg': 'Your school',
  'classList.meta': '{classes} classes · {students} students',
  'classList.section.all': 'All classes',
  'classList.empty.create.title': 'Create your first class',
  'classList.empty.create.body':
    'A class gives your students a join code and a home for every module, assessment, and tutoring session. You can make one per section, subject, or term.',
  'classList.empty.create.cta': 'New class',
  'classList.resumeCard.eyebrow': 'Jump back in',
  'classList.resumeCard.sub': '{subject} · last opened {date}',
  'classList.resumeCard.subNoSubject': 'Last opened {date}',
  'classList.resumeCard.statStudents': 'Students',
  'classList.resumeCard.statModules': 'Modules',
  'classList.resumeCard.statMastery': 'Mastery',
  'classList.resumeCard.code': 'Class code',
  'classList.resumeCard.open': 'Open class →',
  // Subject labels (per SUPPORTED_SUBJECTS + design's four canonical
  // subjects). Capitalized human-readable variants of the registry key.
  'subject.math': 'Mathematics',
  'subject.science': 'Science',
  'subject.english': 'English',
  'subject.history': 'History',
  'subject.misc': 'Miscellaneous',
  'teacher.students.section': 'Enrolled students',
  'teacher.students.addPlaceholder': 'Enter student ID',
  'teacher.students.addButton': 'Add student',
  'teacher.students.delete': 'Delete',
  'teacher.students.deleteConfirm': 'Delete this student?',
  'teacher.students.empty': 'No students enrolled yet.',

  // ── ClassCodeBar ───────────────────────────────────────────
  'classCode.label': 'Class code:',
  'classCode.placeholder': 'Press the generate button',
  'classCode.generate': 'Generate class code',
  'classCode.generating': 'Generating…',
  'classCode.generateError': 'Failed to create class: ',
  'classCode.shareHint': 'Share this code with your students:',
  'classCode.copy': 'Copy',
  'classCode.copied': 'Copied!',

  // ── ModuleTable ────────────────────────────────────────────
  'modules.title': 'Modules',
  'modules.col.name': 'Name',
  'modules.col.subject': 'Subject',
  'modules.col.pdf': 'PDF upload',
  'modules.col.note': 'Teacher note',
  'modules.col.status': 'Status',
  'modules.col.actions': 'Actions',
  'modules.col.order': 'Order',
  'modules.addButton': '+ Add module',

  // ── ModuleRow ──────────────────────────────────────────────
  'module.namePlaceholder': 'Module name',
  'module.notePlaceholder': 'Teacher note…',
  'module.pdfNone': 'No PDF',
  'module.deleteConfirm': 'Delete this module?',
  'module.save': 'Save',
  'module.saving': 'Saving…',
  'module.delete': 'Delete',
  'module.nameRequired': 'Please enter a module name',
  'module.saveFailed': 'Save failed',
  'module.deleteFailed': 'Delete failed',
  'module.saveError': 'Error saving module: ',
  'module.errorPrefix': 'Error: ',
  'module.status.pending': 'Queued',
  'module.status.extracting': 'Extracting text…',
  'module.status.building': 'Building…',
  'module.status.ready': 'Ready',
  'module.status.failed': 'Build failed',
  'module.subject.misc': 'General',
  'module.subject.math': 'Math',
  'module.subject.science': 'Science',
  'module.subject.english': 'English',
  'module.subject.history': 'History',
  // ADR 0040 chunk 5 — reorder affordance on each module row.
  // Up/down arrows interpolate the module name so screen readers
  // announce "Move Polynomials up" instead of just "Move up".
  'module.reorder.up': 'Move {name} up',
  'module.reorder.down': 'Move {name} down',
  'module.reorderError': 'Error reordering module: ',
  'module.reorderFailed': 'Reorder failed',

  // ── StudentAssessments ─────────────────────────────────────
  'assessments.title': 'Student assessments',
  'assessments.empty':
    'No assessments yet. Results will appear here after students finish a tutoring session.',
  'assessments.strengths': 'Strengths:',
  'assessments.weaknesses': 'Weaknesses:',
  'assessments.learningStyle': 'Learning style:',

  // ── ChatMessage ────────────────────────────────────────────
  'chat.role.tutor': 'Tutor',
  'chat.role.self': 'You',

  // ── TutoringSession ────────────────────────────────────────
  'session.loading': 'Loading session…',
  // #623 — session-load failure recovery panel. Pre-fix the catch in
  // TutoringSession's load effect only fired ``alert(session.loadError)``
  // and stranded the student on an infinite "Loading session…" view
  // with no retry / back affordance. The panel now renders these in
  // place of the bare loading text. ``notFound`` is narrowed from
  // ``isApiError(e) && e.status === 404`` (cross-student access,
  // stale link, deleted session); ``generic`` covers 5xx + network.
  'session.loadError.notFound.title': "We can't find this session",
  'session.loadError.notFound.body':
    "This session is either no longer available or doesn't belong to you. The link may be out of date — head back to your lessons to start a new one.",
  'session.loadError.generic.title': "Couldn't load this session",
  'session.loadError.generic.body':
    "Something went wrong while loading your session. Check your connection and try again — if it keeps failing, head back to your lessons.",
  'session.loadError.retry': 'Try again',
  'session.loadError.back': 'Back to my lessons',
  // Phase-transition intermediates (issue #142). Shown briefly while
  // the LLM round-trip lands; the visible chrome (session-bar +
  // gradient backdrop) persists across phases so only the inner panel
  // fades.
  'session.preparingTutor': 'Preparing your AI tutor…',
  'session.wrappingUp': 'Wrapping up your session…',
  // Inline success feedback shown next to the assessment submit button
  // immediately on phase-advance, before the assessment view fades out.
  'session.assessmentSubmitted': 'Submitted',
  'session.lastAssessmentSubmitted': 'Final question submitted!',
  'session.assessment.title': 'Level assessment',
  // #204 — aria-labels for the per-answer feedback banner's icon. The
  // visible content is the backend-generated feedback string itself;
  // these only feed screen readers via aria-label on the icon span.
  'session.assessment.feedbackCorrect': 'Correct',
  'session.assessment.feedbackIncorrect': 'Incorrect',
  // #847 — the eval-failed TRANSIENT banner. ``evaluate_answer`` failed
  // (provider error / parse / schema reject), so the verdict is UNKNOWN
  // and the same question is re-asked. The amber ⚠ banner renders the
  // backend's transient message; this key is its icon aria-label.
  'session.assessment.feedbackEvalFailed': "Couldn't evaluate",
  // Escalated hint appended after N (≥3) consecutive eval failures so a
  // systematic outage doesn't read as an infinite "try again" loop.
  'session.assessment.feedbackEvalFailedPersistent':
    "We're still having trouble scoring your answer — please try again in a little while.",
  // #848 — system-wide assessment-grading incident banner (vs the
  // per-student transient banner above). Shown at the top of the session
  // when the platform is seeing an elevated number of grading failures
  // across students (e.g. a provider outage). Warm + reassuring, no
  // numbers, no internal jargon — "we see it and we're on it".
  'session.incident.title': "Grading is a little shaky right now",
  'session.incident.body':
    "We're currently seeing some reliability issues with answer grading and we're on it. Feel free to keep learning — grading should be back to normal shortly.",
  'session.assessment.intro':
    'Answer the following questions so we can gauge your current understanding.',
  'session.bar.crumbsRoot': 'Modules',
  'session.tutorName': 'eehae tutor',
  'session.tutorSub': 'Tutoring',
  'session.live': 'Live',
  'session.answerPlaceholder': 'Type your answer…',
  'session.loadingQuestions': 'Loading questions…',
  'session.submit': 'Submit answers',
  'session.submitting': 'Submitting…',
  'session.tutoring.title': 'Tutoring session',
  'session.end': 'End session',
  'session.endConfirm': 'End this session?',
  'session.send': 'Send',
  'session.sending': 'Sending…',

  // ADR 0030 Phase 6 — Notes-prose
  'session.openNotes': 'Open notes',
  'notes.tutorTab': 'Tutor',
  'notes.notesTab': 'Notes',
  'notes.versionLabel': 'Version',
  'notes.backToTutor': 'Back to tutor',
  'notes.tocLabel': 'Contents',
  'notes.tldrLabel': 'TL;DR',
  'notes.tldrCta': 'If you read nothing else',
  'notes.collapse': 'Collapse',
  'notes.expand': 'Expand',
  'notes.sourceLabel': 'Source',
  'notes.sourceDescription': 'Original PDF material',
  'notes.moduleCrumb': 'My learning / Module',
  'notes.askAbout': 'Ask about this',
  'notes.askPrompt': 'Stuck on this section? Ask in-line.',
  'notes.askPlaceholder': 'e.g. Why does chlorophyll look green?',
  'notes.askSend': 'Ask the tutor',
  'notes.askDisabledHint': 'This feature ships in a follow-up release',
  'notes.askFootnote': 'Your tutor is reading along. Questions are answered with §-references back into the note.',
  'notes.loadError': 'Could not load the notes.',
  'notes.notReadyTitle': 'Your notes aren’t ready yet',
  'notes.notReadyBody': 'Notes for this module are generated once the module build completes.',
  'notes.backToSession': 'Back to session',
  'notes.citationFallback': 'source',
  // Per-student note re-prioritization (#1207 / #1176 §3c). Surfaced
  // only when the student has weak concepts the note covers.
  'notes.focusBadge': 'For you',
  'notes.focusIntro':
    'These sections cover concepts you haven’t mastered yet — they’re highlighted so you can focus your review.',
  'notes.focusSectionBadge': 'Review this',
  'notes.focusTocMarker': 'Concept to review',
  'session.inputPlaceholder':
    'Type a message. Shift+Enter for a new line.',
  // Quick-action buttons under the tutoring input (ADR 0055). The
  // label doubles as the student's transcript bubble for the press.
  'session.action.explain': 'I’m not sure — please explain',
  'session.action.skip': 'Skip this question',
  'session.action.report': 'Something’s wrong with this question',
  'session.action.reportConfirm':
    'Report this question? The concept will be removed from your learning list and your teacher will review it.',
  'session.errorPrefix': 'Error: ',
  // Streaming-failover (#280). The primary LLM provider failed mid-
  // stream, the backend is re-running against the next provider in
  // the chain. Brief beat shown above the (just-cleared) assistant
  // bubble so the student perceives "the AI took a moment to
  // reconsider" rather than a hiccup. Cleared as soon as the first
  // delta from the fallback lands.
  'session.reconsidering': 'Reconsidering…',
  // SSE chunk-timeout recovery (#163 item 3). The tutoring stream went
  // silent past the 15s chunk-timeout — no token AND no backend
  // heartbeat — so the connection is treated as lost. `connectionRetrying`
  // shows during the automatic single retry; `connectionLost` +
  // `retry` surface once that retry has also gone silent and the student
  // gets a manual Retry. Replaces the prior infinite "Waiting for
  // tutor…" hang with an explicit, recoverable state.
  'session.connectionRetrying': 'Connection lost — retrying…',
  'session.connectionLost':
    "Connection lost. Your message wasn't sent.",
  'session.retry': 'Retry',
  // 409 conflict — another concurrent action (different tab, network
  // retry) bumped the session's version while this submit was in flight.
  // Backend's #106 optimistic-locking surfaces this instead of silently
  // clobbering the other writer's state.
  'session.staleConflict':
    'This session was updated elsewhere. Refresh the page to load the latest state?',
  // Session JSON-column corruption (#108). Engine state failed to
  // deserialize — probably a backend crash mid-write or a schema
  // migration mismatch. Backend marks the session ``corrupted`` and
  // returns it; we render this panel as the explicit dead-end so the
  // student knows to start a new session instead of seeing a generic
  // 500. Old messages remain in the DB for forensics.
  'session.corrupted.title': "We can't continue this session",
  'session.corrupted.body':
    'Something went wrong with this session\'s data and it can\'t continue. Please start a new session for the same module. Your previous answers are safely stored.',
  'session.corrupted.cta': 'Start a new session',
  'session.complete.title': 'Today\'s session summary',
  'session.complete.headline': 'You moved one step forward today',
  'session.complete.sub':
    'Nice work. Here\'s a short read on what we noticed in this session.',
  'session.complete.resultTitle': 'Assessment result',
  'session.complete.strengths': 'Strengths',
  'session.complete.report': 'Session report:',
  'session.complete.weaknesses': 'To revisit',
  'session.complete.learningStyle': 'Learning style:',
  'session.complete.summaryTitle': 'Today\'s summary',
  'session.complete.mastery': 'Mastery:',
  'session.complete.mastery.full': 'Fully mastered',
  'session.complete.mastery.partial': 'Partially mastered',
  'session.complete.mastery.insufficient': 'Needs more practice',
  'session.complete.fallback': 'Session ended.',
  'session.back': 'Back to modules',

  // ── Language switcher ──────────────────────────────────────
  'lang.toggleLabel': '한국어',

  // ── Splash screen ──────────────────────────────────────────
  'splash.subtitle': 'unlock your understanding',
  'splash.loginSubtitle':
    'Enter your school code, student ID, and password to start today\'s tutoring.',
  // Issue #967 — shown above the legacy first-login bootstrap sub-form.
  'splash.loginSubtitleBootstrap':
    "Don't have a password yet? Sign in the first time with your class code and student ID.",
  // ADR 0051 Phase 4 — shown above the forgot-password request sub-form.
  'splash.loginSubtitleForgot':
    'Enter your school and student ID to ask your teacher or administrator for a password reset.',
  'splash.teacher': 'Teacher dashboard',
  // Issue #1104 — the student CTA opens the school + student-ID + password
  // sign-in (a class code is for joining a class after login, not for
  // signing in), so the button reads as a student login, not "I have a code".
  'splash.student': 'Student login',
  'splash.back': 'Back',
  'splash.admin': 'Admin',
  'splash.signedOut': 'Signed out successfully',
  // #832 — shown on the home splash when a signed-in non-admin user
  // tries to access an admin route and is redirected here.
  'splash.notAuthorized':
    'You are not authorized to access administrator pages on this account.',
  'splash.scrollHint': 'Scroll',

  // ── Theme switcher ─────────────────────────────────────────
  'theme.light': 'Light',
  'theme.dark': 'Dark',
  'theme.colorful': 'Colorful',

  // ── Brand ──────────────────────────────────────────────────
  'brand.name': 'eehae',

  // ── Auth split-screen marketing panel ─────────────────────
  'auth.left.role.admin': 'Admin console',
  'auth.left.eyebrow.teacher': 'For teachers',
  'auth.left.eyebrow.admin': 'For school administrators',
  'auth.left.headline.teacher.signin': 'Welcome back to your classes.',
  'auth.left.headline.teacher.signup': 'Teach a thinking machine to teach.',
  'auth.left.headline.admin.signin': 'Run the school — calmly.',
  'auth.left.headline.admin.signup': 'Bring your school onto eehae.',
  'auth.left.subhead.teacher':
    'eehae handles the assessments, the practice, and the patience. You get the room back.',
  'auth.left.subhead.admin':
    "Provision teacher seats, manage billing, and watch the school's pulse — all in one console.",
  'auth.left.foot.privacy': 'Privacy',
  'auth.right.crosslink.signin.teacher.prompt': 'New to eehae?',
  'auth.right.crosslink.signin.teacher.cta': 'Create an account →',
  'auth.right.crosslink.signin.admin.prompt': 'Not provisioning a school?',
  'auth.right.crosslink.signin.admin.cta': 'Teacher sign-in →',
  'auth.right.crosslink.signup.prompt': 'Already have an account?',
  'auth.right.crosslink.signup.cta': 'Sign in →',

  // ── Showcase design system: dashboard chrome (#117/#118) ───
  'nav.section.teaching': 'Teaching',
  'nav.section.classes': 'Classes',
  'nav.section.school': 'School',
  'nav.overview': 'Overview',
  'nav.students': 'Students',
  'nav.modules': 'Modules',
  'nav.assessments': 'Assessments',
  'topbar.search': 'Search students, modules, classes',
  'topbar.invite': 'Invite',
  'topbar.newClass': 'New class',
  'topbar.newClass.creating': 'Creating…',
  'topbar.newClass.error': 'Could not create class: ',
  'topbar.comingSoon': 'Coming soon',
  // ── TeacherTopbar search dropdown (#608) ───────────────────
  'search.group.students': 'Students',
  'search.group.modules': 'Modules',
  'search.group.classes': 'Classes',
  'search.noResults': 'No results',
  'search.loading': 'Searching…',
  'classNameDialog.create.title': 'New class',
  'classNameDialog.rename.title': 'Rename class',
  'classNameDialog.label': 'Class name',
  'classNameDialog.placeholder': 'e.g. Period 3 Algebra II',
  'classNameDialog.hint': 'Up to 100 characters. Leave blank to use only the class code.',
  'classNameDialog.create.submit': 'Create class',
  'classNameDialog.create.submitting': 'Creating…',
  'classNameDialog.rename.submit': 'Save',
  'classNameDialog.rename.submitting': 'Saving…',
  'classNameDialog.cancel': 'Cancel',
  'classNameDialog.error': 'Could not save: ',
  // ── Build-truncation notice dialog (#1225 / #1176 §1b) ──────
  'buildNotice.title': 'Some module material wasn\'t used',
  'buildNotice.intro': 'The material you uploaded was too long to fit in one module, so the build used only the beginning. Affected module(s):',
  'buildNotice.coverage': 'Built from ~{used} of {source} characters (~{dropped} dropped)',
  'buildNotice.renamed': 'Renamed “{original}” → “{applied}”.',
  'buildNotice.recommend': 'Recommended: re-upload the rest as chapter builds, or accept the partial build as-is.',
  'buildNotice.action.accept': 'Accept the partial build',
  'buildNotice.action.keepName': 'Keep my original name',
  'buildNotice.action.rebuild': 'Rebuild as chapters',
  'buildNotice.acknowledging': 'Saving…',
  'buildNotice.error': 'Couldn\'t save. Please try again.',
  'buildNotice.dismiss': 'Dismiss',
  'hero.renameClass': 'Rename',
  'classList.card.rename': 'Rename',
  'classList.card.menuLabel': 'Class actions',
  'hero.eyebrow': 'Today\'s class',
  'hero.classCodeLabel': 'Class code',
  'hero.classCodeCopy': 'Copy',
  // Issue #967 — school login code pill (password-login: school code + student ID + password).
  'hero.schoolCodeLabel': 'School code',
  'hero.schoolCodeCopy': 'Copy',
  'hero.schoolCodeHint': 'Students sign in with this, their student ID, and password.',
  'hero.pulse.active': 'Active this week',
  'hero.pulse.activeHint': 'vs. 7 days ago',
  'hero.pulse.sessions': 'Sessions this week',
  'hero.pulse.sessionsHint': 'sessions per day',
  'hero.pulse.mastery': 'Class mastery',
  'hero.pulse.attention': 'Need attention',
  'hero.pulse.attentionHint': 'students',
  'students.section.title': 'Students',
  'students.section.sub': 'Class roster · mastery at a glance',
  'students.headers.id': 'Student',
  'students.headers.last': 'Last session',
  'students.headers.mastery': 'Mastery',
  'students.headers.flag': 'Status',
  // ── Admin reset-password control (issue #967, PR-3) ────────────
  'students.resetPassword.action': 'Reset password',
  'students.resetPassword.title': 'Reset password',
  'students.resetPassword.subtitle': "Reset {id}'s password.",
  'students.resetPassword.mode.generate': 'Generate a password',
  'students.resetPassword.mode.type': 'Type a password',
  'students.resetPassword.type.placeholder': 'New password',
  'students.resetPassword.type.hint':
    'The password must be at least {n} characters.',
  'students.resetPassword.generated.warning':
    "This password won't be shown again. Copy it now and give it to the student.",
  'students.resetPassword.generated.label': 'Generated password',
  'students.resetPassword.copy': 'Copy',
  'students.resetPassword.copied': 'Copied',
  'students.resetPassword.done': 'Done',
  'students.resetPassword.cancel': 'Cancel',
  'students.resetPassword.submit': 'Reset',
  'students.resetPassword.submitting': 'Resetting…',
  'students.resetPassword.error.generic':
    "Couldn't reset the password. Please try again.",
  'flag.ok': 'On track',
  'flag.watch': 'Watch',
  'flag.behind': 'Behind',
  'flag.new': 'New',
  'modules.section.title': 'Modules',
  'modules.section.sub': 'Learning materials · build status',
  'status.ready': 'Ready',
  'status.extracting': 'Extracting text…',
  'status.building': 'Building',
  'status.failed': 'Failed',
  'status.pending': 'Pending',
  'assess.section.title': 'Assessments',
  'assess.section.sub': 'Strengths and weaknesses per student',
  'assess.filter.attention': 'Need attention',
  'assess.filter.all': 'All',
  'assess.cols.strengths': 'Strengths',
  'assess.cols.weaknesses': 'Weaknesses',
  'assess.expand': 'Details',
  'assess.collapse': 'Hide',
  'assess.level.full': 'Full',
  'assess.level.partial': 'Partial',
  'assess.level.insuff': 'Needs work',

  // ── Admin (issue #31) ──────────────────────────────────────
  'admin.loading': 'Loading…',
  'admin.errorPrefix': 'Error: ',
  'admin.retry': 'Retry',
  'admin.title.fallback': 'Admin',

  'admin.createOrg.title': 'Create your school',
  'admin.createOrg.hint':
    "Create the school (organization) you'll be administrating. You'll pick a billing plan after this step, and invite your teaching staff from the admin dashboard once your school is provisioned.",

  'admin.onboarding.title': 'Finish setting up your school',
  'admin.onboarding.hint':
    "Confirm your school name and pick a plan to finish provisioning. Once you do, you'll be able to invite teachers and they'll be able to create classes.",
  'admin.onboarding.schoolName.label': 'School name',
  'admin.onboarding.schoolName.placeholder': 'e.g. Pine Valley High',
  'admin.onboarding.schoolName.save': 'Save',
  'admin.onboarding.schoolName.saving': 'Saving…',
  'admin.onboarding.plansHeader': 'Plans',
  'admin.onboarding.checkoutNote':
    "Secure payment processing via Stripe through Clerk. You'll be returned here once checkout completes.",
  'admin.onboarding.activating.title': 'Activating your subscription…',
  'admin.onboarding.activating.hint':
    "Hang tight — this usually takes just a few seconds once payment is confirmed.",

  // ── PIPA P2 (#128) chunk 3 ─────────────────────────────────────
  // English mirror of the Korean-primary notice / warranty screens.
  // The Korean strings are placeholders pending pedagogy + legal
  // review; the English mirror follows the same shape.

  // Student first-login Art. 22-2 §3 plain-language notice.
  'student.notice.title': 'Hey, quick heads-up',
  'student.notice.subtitle':
    'Before you use eehae, here are a few things to know.',
  'student.notice.what.heading': 'What do we collect?',
  'student.notice.what.body':
    'Your answers in eehae, your progress, and your name + student ID. Nothing else.',
  'student.notice.who.heading': 'Who handles it?',
  'student.notice.who.body':
    'Your school (or hagwon) is in charge of your information. eehae helps the school handle it safely.',
  'student.notice.where.heading': 'Where does it go?',
  'student.notice.where.body':
    'Your answers go to an AI service that helps with learning. For details, ask your teacher or read the privacy policy.',
  'student.notice.rights.heading': 'What can I do?',
  'student.notice.rights.body':
    'You can ask to see or fix your information at any time. Talk to your teacher or parent.',
  'student.notice.acknowledge': 'I understand',
  'student.notice.acknowledging': 'One moment…',
  'student.notice.error':
    "Couldn't save your acknowledgment. Please try again in a moment.",

  // ── Student first-login set-password screen (issue #967, PR-3) ──
  'student.setPassword.title': 'Create your password',
  'student.setPassword.subtitle':
    "From now on you'll sign in with your school code, student ID, and this password.",
  'student.setPassword.new.label': 'New password',
  'student.setPassword.new.placeholder': 'Enter a password',
  'student.setPassword.confirm.label': 'Confirm password',
  'student.setPassword.confirm.placeholder': 'Enter your password again',
  'student.setPassword.hint': 'Make it at least {n} characters.',
  'student.setPassword.submit': 'Save password',
  'student.setPassword.submitting': 'Saving…',
  'student.setPassword.error.tooShort':
    'Your password must be at least {n} characters.',
  'student.setPassword.error.mismatch': "The two passwords don't match.",
  'student.setPassword.error.generic':
    "Couldn't save your password. Please try again in a moment.",

  // Teacher / admin warranty acceptance (school-as-controller).
  'warranty.title': 'School operator confirmation',
  'warranty.hint':
    'Before continuing, please confirm both items. Both checkboxes are required to proceed.',
  'warranty.under14.label':
    'Under-14 guardian consent obtained (PIPA Art. 22-2)',
  'warranty.under14.body':
    'We confirm that for under-14 students, guardian consent for personal-information collection and use was obtained at enrollment.',
  'warranty.subprocessor.label':
    'AI sub-processor consent (PIPA Art. 26 §6)',
  'warranty.subprocessor.body':
    'We acknowledge and consent to the learning-support AI sub-processor handling student data. (See the privacy policy for sub-processor details.)',
  'warranty.submit': 'Confirm and continue',
  'warranty.submitting': 'Saving…',
  'warranty.error':
    "Couldn't save your confirmation. Please try again in a moment.",
  'warranty.alreadyAccepted': 'Already confirmed',
  'warranty.policyVersion': 'Policy version: {version}',

  // Tier 3 DPA click-through (PIPA P2 #128 chunk 4). PLACEHOLDER
  // until legal counsel review.
  'dpa.title': 'Data Processing Agreement (DPA)',
  'dpa.hint':
    'Please review the full DPA document before checking the box below.',
  'dpa.viewTemplate': 'View the full DPA document →',
  'dpa.tier3.label': 'Data Processing Agreement acceptance (Tier 3)',
  'dpa.tier3.body':
    'The institution has read and understood the full Data Processing Agreement (DPA) above and agrees to all of its terms.',
  'dpa.submit': 'Accept and continue',
  'dpa.submitting': 'Saving…',
  'dpa.error':
    "Couldn't save your DPA acceptance. Please try again in a moment.",

  'admin.plan.starter': 'Starter',
  'admin.plan.pro': 'Pro',
  'admin.plan.school': 'School',
  'admin.plan.enterprise_pricing': 'Enterprise',
  'admin.plan.enterprise_contract': 'Enterprise',

  'admin.dashboard.stats.plan': 'Plan',
  'admin.dashboard.stats.teacherCap': 'Teacher cap',
  'admin.dashboard.stats.studentCap': 'Student cap',
  'admin.dashboard.stats.none': '—',
  'admin.dashboard.stats.unlimited': 'Unlimited',
  // Mastery KPI tile (#198). ``noData`` shows under the em-dash when
  // the school has no profiles yet; ``basis`` shows under the percent
  // when there's data. {n} = students with profile, {total} = roster.
  'admin.dashboard.stats.mastery': 'Avg. mastery',
  'admin.dashboard.stats.mastery.noData': 'No data yet',
  'admin.dashboard.stats.mastery.basis': 'Based on {n} of {total} students',
  'admin.dashboard.members.title': 'Members & invitations',
  'admin.dashboard.members.hint':
    "Invite teachers here. They'll receive an email from Clerk; once they accept and sign up, they'll auto-join your school and can create classes.",
  'admin.dashboard.members.devNotice':
    'Member management is only available in a real Clerk organization session, so it’s hidden in the dev quickstart session.',
  // #848 — admin-facing assessment-grading incident banner. Informative
  // variant (count / window) so an operator can judge severity, vs the
  // student banner's number-free reassurance. Reliability signal only —
  // never cost / tokens (the #442 invariant). {count} / {minutes} /
  // {threshold} are interpolated via t(key, vars).
  'admin.incident.title': 'Elevated assessment grading incidents',
  'admin.incident.body':
    '{count} assessment grading failures in the last {minutes} minutes (threshold {threshold}). This may be an external model-provider outage — check status.',

  // ── Admin dashboard shell (sidebar / topbar / hero / class list / pending rail) ──
  'admin.nav.classes': 'Classes',
  'admin.nav.members': 'Members',
  'admin.topbar.searchPlaceholder': 'Search students, classes, invoices…',
  'admin.topbar.invite': 'Invite teacher',
  'admin.hero.eyebrow': 'School at a glance',
  'admin.hero.welcome': "Here's how your school is running today.",
  'admin.hero.seats.teachers': 'Teacher seats',
  'admin.hero.seats.students': 'Student seats',
  'admin.classes.eyebrow': 'Classes',
  'admin.classes.title': 'All classes in your school',
  'admin.classes.empty': 'No classes have been set up yet.',
  'admin.classes.col.class': 'Class',
  'admin.classes.col.teacher': 'Teacher',
  'admin.classes.col.students': 'Students',
  'admin.classes.col.mastery': 'Mastery',
  'admin.pending.eyebrow': 'Pending',
  'admin.pending.title': 'Unaccepted invitations',
  'admin.pending.empty': 'No invitations are waiting.',
  'admin.pending.sent': 'Sent {date}',

  // ── ADR 0051 Phase 5: admin student management (#1091, closes #985) ──
  'admin.nav.students': 'Students',
  'admin.students.eyebrow': 'Student management',
  'admin.students.title': 'Student roster',
  'admin.students.subtitle':
    'The admin creates student accounts; teachers and students handle class membership.',
  'admin.students.loading': 'Loading the roster…',
  'admin.students.empty': 'No students yet. Add one below.',
  'admin.students.error': 'Could not load the roster.',
  'admin.students.count': '{n} students',
  // Roster table
  'admin.students.col.id': 'Student ID',
  'admin.students.col.created': 'Created',
  'admin.students.col.classes': 'Classes',
  'admin.students.col.status': 'Status',
  'admin.students.col.actions': 'Actions',
  'admin.students.noClasses': 'No class',
  'admin.students.badge.noPassword': 'No password',
  'admin.students.badge.pendingReset': 'Reset requested',
  // Create-one form
  'admin.students.create.label': 'Create a student account',
  'admin.students.create.placeholder': 'Student ID',
  'admin.students.create.button': 'Create account',
  'admin.students.create.submitting': 'Creating…',
  'admin.students.create.hint':
    'Avoid sequential or easily-guessable IDs (e.g. 1, 2, 3). Pasting raw student-ID numbers weakens first-login security.',
  'admin.students.create.success': 'Created account for {id}.',
  'admin.students.create.error.generic':
    'Could not create the account. Please try again.',
  // CSV roster import (closes #985)
  'admin.roster.import.title': 'Bulk import from CSV',
  'admin.roster.import.hint':
    'The student_id column is required; class_code is optional. Rows without class_code create unassigned accounts. (Excel: save as "CSV UTF-8".)',
  'admin.roster.import.choose': 'Choose CSV file',
  'admin.roster.import.button': 'Upload',
  'admin.roster.import.submitting': 'Uploading…',
  'admin.roster.import.tooLarge': 'File is too large (max 5MB).',
  'admin.roster.import.error.generic':
    'Bulk import failed. Check the file and try again.',
  'admin.roster.import.result.title': 'Import result',
  'admin.roster.import.result.created': '{n} new students enrolled',
  'admin.roster.import.result.enrolled': '{n} existing students enrolled',
  'admin.roster.import.result.unassigned': '{n} new students (no class)',
  'admin.roster.import.result.skipped': '{n} already existed',
  'admin.roster.import.result.errors': '{n} errors',
  'admin.roster.import.result.errorRow': 'Row {row} ({id}): {message}',
  // Teacher-create toggle (School.allow_teacher_student_creation)
  'admin.settings.teacherCreate.label': 'Let teachers create student accounts',
  'admin.settings.teacherCreate.hint':
    'When off, teachers can only add students the admin has created. When on, teachers can mint new student IDs themselves.',
  'admin.settings.teacherCreate.saving': 'Saving…',
  'admin.settings.teacherCreate.error': 'Could not save the setting.',
  // Forgot-password request queue rail (Phase 3 → admin resolves)
  'admin.passwordReset.eyebrow': 'Pending',
  'admin.passwordReset.title': 'Password reset requests',
  'admin.passwordReset.empty': 'No pending reset requests.',
  // Cold-load failure copy — distinct from ``empty`` so a fetch error
  // can't masquerade as "queue is clear" (CodeRabbit on #1291). Mirrors
  // ``admin.students.error`` phrasing.
  'admin.passwordReset.error': 'Could not load reset requests.',
  'admin.passwordReset.requested': 'Requested {date}',
  'admin.passwordReset.action': 'Reset & resolve',

  // ── #442: customer-facing ops dashboard (/admin/ops) ──────────────
  // CUSTOMER surface — build health / session throughput / PIPA DSR
  // activity / deploy info only. NOTHING LLM-economics-related (tokens,
  // providers, cost, call counts, LLM error rates) may appear in this
  // copy; that lives on operator-only Grafana, a fully separate surface.
  'admin.nav.ops': 'Operations',
  'admin.ops.title': 'Operations',
  // {hours} = window_hours from the API (24 today, but server-driven).
  'admin.ops.subtitle':
    'How your school has been running over the last {hours} hours.',
  'admin.ops.loading': 'Loading the operational summary…',
  'admin.ops.error': 'Could not load the operational summary.',
  'admin.ops.retry': 'Try again',
  'admin.ops.none': '—',
  'admin.ops.build.title': 'Module build health',
  'admin.ops.build.inFlight': 'In flight',
  'admin.ops.build.succeeded': 'Succeeded',
  'admin.ops.build.failed': 'Failed',
  'admin.ops.build.recentFailures': 'Recent failures',
  'admin.ops.build.noFailures': 'No failed builds recently.',
  // {phase} = localized build-phase label (below), {attempt} = attempt ordinal.
  'admin.ops.build.failureMeta': '{phase} phase · attempt {attempt}',
  'admin.ops.build.retryAria': 'Open the build-phases page for {name}',
  // Build-phase labels. Tokens mirror BUILD_PHASES in
  // V3/backend/services/module_build/tracker.py; AdminOps.tsx falls
  // back to the raw token for any vocabulary drift.
  'admin.ops.build.phase.concepts': 'Concepts',
  'admin.ops.build.phase.questions': 'Questions',
  'admin.ops.build.phase.examples': 'Worked examples',
  'admin.ops.build.phase.misconceptions': 'Misconceptions',
  'admin.ops.build.phase.notes': 'Study notes',
  'admin.ops.sessions.title': 'Learning sessions',
  'admin.ops.sessions.started': 'Started',
  'admin.ops.sessions.completed': 'Completed',
  'admin.ops.sessions.avgDuration': 'Avg. duration',
  'admin.ops.sessions.avgValue': '{minutes} min',
  // Shown when avg_duration_minutes is null (nothing completed in the
  // window) — deliberately distinct from a real 0-minute average.
  'admin.ops.sessions.noCompleted': 'No sessions completed',
  'admin.ops.audit.title': 'Data-rights activity',
  'admin.ops.audit.exports': 'Exports',
  'admin.ops.audit.modifications': 'Modifications',
  'admin.ops.audit.deletions': 'Deletions',
  'admin.ops.audit.hint':
    'Data-subject-rights actions (access, correction, deletion) handled by your school’s staff under PIPA.',
  'admin.ops.deploy.title': 'Deploy info',
  'admin.ops.deploy.release': 'Release',
  'admin.ops.deploy.releaseUnknown': 'Unknown',
  // Honest label: the API reports PROCESS START time, which on Fly is
  // within seconds of deploy completion but is not literally "deployed
  // at" — see DeployInfo in V3/backend/routers/admin.py.
  'admin.ops.deploy.processStarted': 'Server process started',
  'admin.ops.deploy.processStartedHint':
    'Deploying a new version restarts the server process, so this is effectively when the running version was last deployed.',

  // ── #408 Area B: per-module build-phase ladder ─────────────────────
  // (/admin/modules/{id}/build/phases — pages/AdminBuildPhases.tsx)
  'admin.buildPhases.title': 'Module build phases',
  // {id} = module id from the URL (echoed back by the API).
  'admin.buildPhases.subtitle':
    'Per-phase build status for module #{id}. Retrying a phase re-runs just that step without rebuilding the others.',
  'admin.buildPhases.loading': 'Loading the build ladder…',
  'admin.buildPhases.error': 'Could not load the build ladder.',
  'admin.buildPhases.reload': 'Try again',
  'admin.buildPhases.invalidModule': 'The module id in the URL is not valid.',
  // Empty ladder is a legitimate state — the module pre-dates the
  // per-phase tracking table or hasn't been built yet (see the GET
  // endpoint docstring in V3/backend/routers/admin.py).
  'admin.buildPhases.empty':
    'No phase data has been recorded for this module yet. Modules built before per-phase tracking landed have no ladder.',
  'admin.buildPhases.ladderTitle': 'Current status by phase',
  // Phase names mirror BUILD_PHASES in services/module_build/tracker.py.
  'admin.buildPhases.phase.concepts': 'Concepts',
  'admin.buildPhases.phase.questions': 'Questions',
  'admin.buildPhases.phase.examples': 'Worked examples',
  'admin.buildPhases.phase.misconceptions': 'Misconceptions',
  'admin.buildPhases.phase.notes': 'Notes',
  'admin.buildPhases.status.pending': 'Pending',
  'admin.buildPhases.status.running': 'Running',
  'admin.buildPhases.status.succeeded': 'Succeeded',
  'admin.buildPhases.status.failed': 'Failed',
  // 'skipped' ≠ 'failed': the phase deliberately did NOT run because
  // an upstream dependency was missing (orm/build.py status semantics)
  // — the operator retries the upstream, not the skipping phase.
  'admin.buildPhases.status.skipped': 'Skipped',
  // {attempt} = attempt ordinal (matches admin ops failureMeta style).
  'admin.buildPhases.attempt': 'attempt {attempt}',
  'admin.buildPhases.elapsed': '{seconds}s',
  'admin.buildPhases.none': '—',
  'admin.buildPhases.retry': 'Retry phase',
  'admin.buildPhases.retryAria': 'Retry the {phase} phase',
  'admin.buildPhases.retrying': 'Retrying…',
  // 200-but-failed: the retry RAN and the phase failed again — points
  // the operator at the row's error text, distinct from retryError
  // (the POST itself was rejected: 409 upstream missing, 5xx, ...).
  'admin.buildPhases.retryFailed':
    'The {phase} phase ran again but failed — see the error on its row.',
  // 200-but-skipped: the retry deliberately did NOT run (upstream
  // artifact missing / cap-saturated) — the row's note explains why.
  'admin.buildPhases.retrySkipped':
    'The {phase} phase was skipped without running — see the note on its row.',
  'admin.buildPhases.retryError': 'Could not retry the phase.',
  'admin.buildPhases.historyTitle': 'Attempt history',
  'admin.buildPhases.history.when': 'When',
  'admin.buildPhases.history.phase': 'Phase',
  'admin.buildPhases.history.status': 'Status',
  'admin.buildPhases.history.attempt': 'Attempt',
  'admin.buildPhases.history.elapsed': 'Elapsed',

  // ── Dev: session transcripts ───────────────────────────────
  'dev.transcripts.title': 'Session transcripts (dev)',
  'dev.transcripts.empty': 'No sessions recorded yet.',
  'dev.transcripts.loading': 'Loading…',
  'dev.transcripts.messagesSuffix': 'messages',

  // ── Dev: quickstart harness ───────────────────────────────
  'dev.quickstart.title': 'Dev quickstart',
  'dev.quickstart.subtitle':
    'Spin up a seeded test school + teacher + student so you can skip Clerk signup and billing while testing.',
  'dev.quickstart.passwordLabel': 'Password',
  'dev.quickstart.passwordPlaceholder': 'Enter the password',
  'dev.quickstart.submit': 'Start test environment',
  'dev.quickstart.submitting': 'Preparing…',
  'dev.quickstart.errorInvalid': 'Wrong password',
  'dev.quickstart.errorGeneric': 'Failed to start test environment: ',
  'dev.quickstart.readyTitle': 'Ready',
  'dev.quickstart.schoolLabel': 'School',
  'dev.quickstart.teacherLabel': 'Teacher',
  'dev.quickstart.classCodeLabel': 'Class code',
  'dev.quickstart.studentLabel': 'Student',
  'dev.quickstart.openTeacher': 'Open teacher dashboard',
  'dev.quickstart.openStudent': 'Open student view',
  'dev.quickstart.exit': 'End test session',
  'dev.quickstart.notice':
    'This page is only available when the backend has DEV_TOOLS=1. It does not replace real user authentication.',
  'dev.quickstart.banner': 'Dev test session active',
  'dev.quickstart.bannerExit': 'Exit',
  'dev.quickstart.cohortTitle': 'Demo cohort',
  'dev.quickstart.cohortSub':
    'Populate the dev class with mock students + sessions so the teacher dashboard reads as a real school. Idempotent — re-seeding clears the previous batch first.',
  'dev.quickstart.cohortSeed': 'Seed demo students',
  'dev.quickstart.cohortClear': 'Clear demo students',
  'dev.quickstart.cohortBusy': 'Working…',
  'dev.quickstart.cohortSeeded': 'Seeded {n} demo students.',
  'dev.quickstart.cohortCleared': 'Cleared {n} demo students.',
  'dev.quickstart.cohortError': 'Cohort op failed: ',
  'dev.quickstart.skipToTutoring': 'Skip to tutoring',
  'dev.quickstart.skipToAssessmentEnd': 'Jump to assessment end',
  'dev.quickstart.skipToAssessmentEndBusy': 'Preparing assessment…',
  // Strings added for the redesign (claude.ai/design handoff,
  // 2026-04-30): split-layout DemoEnvPanel + new ready-state hero
  // card + skip-to callouts + collapsible cohort seeder.
  'dev.quickstart.demoEnv': 'Demo Environment',
  'dev.quickstart.demoTagline':
    "A working copy of the tutoring app, seeded with a demo school, teacher, and student. Nothing here ships; when you exit, all of it goes away.",
  'dev.quickstart.backendStatus': 'Backend running locally',
  // Replaces backendStatus on the redesigned DemoEnvPanel — pairs with
  // the actual ``window.location.host`` value so the panel doesn't
  // mis-claim "running locally" on prod / staging deploys.
  'dev.quickstart.host': 'Host',
  'dev.quickstart.demoQuote1': 'why does this work?',
  'dev.quickstart.demoQuote2': 'show me an example',
  'dev.quickstart.demoQuote3': "I'm stuck on step 3",
  'dev.quickstart.demoQuote4': "what's a derivative?",
  'dev.quickstart.demoQuote5': 'can you go slower?',
  'dev.quickstart.eyebrow': 'Test Harness',
  'dev.quickstart.heading': 'Spin up a session',
  'dev.quickstart.description':
    'Enter the harness password to mint a teacher token, seed a class, and prepare a real student session. Both views open pre-authenticated.',
  'dev.quickstart.sessionActive': 'Session active',
  'dev.quickstart.seededClass': 'Seeded class',
  'dev.quickstart.classCodeNote':
    'Same code the student sees on the splash login.',
  'dev.quickstart.schoolSettings': 'School settings',
  'dev.quickstart.rename': 'Rename school',
  'dev.quickstart.renameSave': 'Save',
  'dev.quickstart.renameSaving': 'Saving…',
  'dev.quickstart.renameCancel': 'Cancel',
  'dev.quickstart.renameError': "Couldn't rename school: ",
  'dev.quickstart.copy': 'Copy',
  'dev.quickstart.copied': 'Copied',
  'dev.quickstart.asTeacher': 'As teacher',
  'dev.quickstart.asStudent': 'As student',
  'dev.quickstart.asAdmin': 'As admin',
  'dev.quickstart.openDashboard': 'Open the dashboard',
  'dev.quickstart.openPicker': 'Open student dashboard',
  'dev.quickstart.openAdminDashboard': 'Open admin dashboard',
  'dev.quickstart.dashboardDesc':
    'Manage modules, view assessments, see student transcripts.',
  'dev.quickstart.pickerDesc':
    'Real student flow with built modules ready to start.',
  'dev.quickstart.adminDashboardDesc':
    'Review school overview, seat usage, and per-class mastery. Member management needs a real Clerk session.',
  'dev.quickstart.activeStudentLabel': 'Active student',
  'dev.quickstart.activeStudentNoneAuth':
    'No active session — enter the password below to provision the dev student.',
  'dev.quickstart.openLink': 'Open',
  'dev.quickstart.openShortcut': 'Open →',
  'dev.quickstart.skipToTutoringSub':
    'Skip past assessment and jump straight to the tutoring chat.',
  'dev.quickstart.skipToAssessmentEndSub':
    'Land on the last assessment question, ready to submit.',
  'dev.quickstart.cohortOptional': 'Optional',
  'dev.quickstart.cohortSummary':
    'Populate dashboard with 16 mock students',

  // ── Marketing page extension (below splash, public-facing) ────────

  'mkt.nav.problem': 'Why',
  'mkt.nav.audiences': 'Who it\'s for',
  'mkt.nav.trust': 'Data & trust',
  'mkt.nav.how': 'How it works',
  'mkt.nav.cta': 'Request a pilot',
  // Progress indicator (vertical dot rail in the choreographed
  // landing). Existing ``mkt.nav.*`` keys cover S1/S3/S4/S5/pilot;
  // these two cover the splash and S2 which have no nav entry.
  'mkt.progress.top': 'Top',
  'mkt.progress.demo': 'In class',

  'mkt.s1.eyebrow': 'Teacher-led AI',
  'mkt.s1.headline.line1': 'What if one teacher',
  'mkt.s1.headline.line2': 'could teach every student',
  'mkt.s1.headline.accent': 'one-on-one',
  'mkt.s1.headline.line3': '.',
  'mkt.s1.body':
    'eehae is a teacher-led AI tutoring platform. Every student gets a personal tutor, and every teacher gets one clear view of the whole class.',
  'mkt.s1.diagram.beforeLabel': 'Before',
  'mkt.s1.diagram.beforeTitle.line1': 'One teacher',
  'mkt.s1.diagram.beforeTitle.line2': 'serving many students',
  'mkt.s1.diagram.afterLabel': 'With eehae',
  'mkt.s1.diagram.afterTitle.line1': 'Every student gets personalized 1-on-1 tutoring,',
  'mkt.s1.diagram.afterTitle.line2': 'after every lecture.',
  'mkt.s1.diagram.invertLabel': 'INVERTS',
  'mkt.s1.diagram.teacher': 'T',
  'mkt.s1.diagram.teacherUnder': 'TEACHER',
  'mkt.s1.diagram.studentsUnder': 'STUDENTS',
  'mkt.s1.diagram.lecturing': 'LECTURING',
  'mkt.s1.diagram.tutoring': '1:1 TUTORING',

  'mkt.s2.headline':
    'Right after class, students prove they understood it.',
  'mkt.s2.body.p1.lead':
    'Students don\'t take eehae home for homework. ',
  'mkt.s2.body.p1.strong':
    'They explain what they just learned, in class, in their own words.',
  'mkt.s2.body.p2':
    'Teachers see who got it and who got stuck, in real time, before class ends.',
  'mkt.s2.body.p3':
    'AI that widens what a teacher can see, not a shortcut around the work.',
  'mkt.s2.studentTag': 'Student',
  'mkt.s2.studentPanelTitle': 'Right after period 3 · 14:42',
  'mkt.s2.chat.ai1':
    'Can you explain photosynthesis in your own words from what we just covered?',
  'mkt.s2.chat.student1':
    'Plants take sunlight and use water and carbon dioxide to make glucose and oxygen.',
  'mkt.s2.chat.ai2': 'Good. So what happens when there isn\'t enough light?',
  'mkt.s2.chat.student2':
    'Hmm… photosynthesis slows down so the plant can\'t grow as well?',
  'mkt.s2.chat.ai3':
    'Right. One more question: is light the only thing photosynthesis needs? What are the other two?',
  'mkt.s2.chat.meta': '2 min 18 sec · 7 turns',
  'mkt.s2.teacherTag': 'Teacher dashboard',
  'mkt.s2.teacherPanelTitle': 'Class 3-2 · Photosynthesis unit',
  'mkt.s2.live': 'Live',
  'mkt.s2.pulse.active': 'Active this week',
  'mkt.s2.pulse.activeValue': '28 / 30',
  'mkt.s2.pulse.sessions': 'Sessions this week',
  'mkt.s2.pulse.sessionsValue': '47',
  'mkt.s2.pulse.mastery': 'Class mastery',
  'mkt.s2.pulse.masteryValue': '73%',
  'mkt.s2.pulse.attention': 'Need attention',
  'mkt.s2.pulse.attentionValue': '4',
  'mkt.s2.pulse.attentionUnit': 'students',

  'mkt.s3.headline':
    'It works differently for schools, teachers, and hagwons.',

  'mkt.s3.school.label': 'School admin',
  'mkt.s3.school.headline':
    'Bring AI to your school, safely.',
  // Bullet 1: mirrors ko.ts — PIPA P2 (#128 / #441) ships
  // Art. 22-2 §3 student notice + institutional warranty + DPA.
  // See ko.ts for the model-shift rationale (school-as-controller,
  // not in-app guardian-consent capture).
  'mkt.s3.school.bullet1':
    'Built-in PIPA Art. 22-2 §3 student notice + institutional warranty + DPA',
  'mkt.s3.school.bullet2':
    'Designated Chief Privacy Officer (CPO)',
  'mkt.s3.school.bullet3':
    'Transparent disclosure of data collected and retention periods',
  'mkt.s3.school.bullet4':
    'Auditable operational records (infrastructure in progress)',
  'mkt.s3.school.fitLabel': 'Best fit if',
  'mkt.s3.school.fit':
    'You want strong student-data protection alongside real learning outcomes.',

  'mkt.s3.teacher.label': 'Teacher',
  'mkt.s3.teacher.headline':
    'AI doesn\'t replace teachers. It extends what they can see.',
  'mkt.s3.teacher.bullet1':
    'See every student\'s progress at a glance',
  'mkt.s3.teacher.bullet2':
    'Auto-detect students who are struggling',
  'mkt.s3.teacher.bullet3':
    'Auto-generate learning modules from your existing materials',
  'mkt.s3.teacher.bullet4':
    'A supplemental tool that never eats into class time',
  'mkt.s3.teacher.fitLabel': 'Best fit if',
  'mkt.s3.teacher.fit':
    'You want a sharper read on each student\'s understanding.',

  'mkt.s3.hagwon.label': 'Hagwon admin',
  'mkt.s3.hagwon.headline':
    'A light AI add-on layered on top of your existing classes.',
  'mkt.s3.hagwon.bullet1': 'Drop-in deployment with no infrastructure to set up',
  'mkt.s3.hagwon.bullet2':
    'Add-on learning service, billable separately from regular tuition',
  'mkt.s3.hagwon.bullet3':
    'Learning-progress reports for parent meetings',
  'mkt.s3.hagwon.bullet4':
    'Integrates naturally with hagwon operations',
  'mkt.s3.hagwon.fitLabel': 'Best fit if',
  'mkt.s3.hagwon.fit':
    'You want differentiated learning outcomes as a competitive edge.',

  'mkt.s4.headline': 'Safe by design, from day one.',
  'mkt.s4.pillar1.title': 'PIPA Compliance',
  'mkt.s4.pillar1.subtitle': '개인정보 보호',
  'mkt.s4.pillar1.body':
    'Explicit student and guardian consent. Clear disclosure of what we collect and how long we keep it. A designated Chief Privacy Officer.',
  'mkt.s4.pillar2.title': 'Processing Location',
  'mkt.s4.pillar2.subtitle': '데이터 처리 위치',
  'mkt.s4.pillar2.body':
    'We disclose where student data is processed. When data leaves Korea, we obtain explicit consent in advance.',
  'mkt.s4.pillar3.title': 'Operational Transparency',
  'mkt.s4.pillar3.subtitle': '투명한 운영',
  'mkt.s4.pillar3.body':
    'What we collect, how we use it, and where we store it: all published and verifiable.',

  'mkt.s5.headline': 'Three steps, starting with a single class.',
  'mkt.s5.step1.title': 'Teacher uploads class materials.',
  'mkt.s5.step1.body':
    'Upload a PDF, lecture notes, or written materials, and eehae builds the learning module.',
  'mkt.s5.step2.title':
    'Students check their understanding right after class.',
  'mkt.s5.step2.body':
    'Each student talks one-on-one with an AI tutor, explaining concepts in their own words.',
  'mkt.s5.step3.title': 'Teacher sees results at a glance.',
  'mkt.s5.step3.body':
    'A dashboard shows who got it and who needs help. Adjust the next class accordingly.',

  'mkt.s6.headline': 'See how eehae works at your school.',
  'mkt.s6.body':
    'Start with one class and one module: a 6-8 week pilot with success criteria set upfront, so you see real results before you commit.',
  'mkt.s6.metaPeriodLabel': 'Pilot length',
  'mkt.s6.metaPeriodValue': '6-8 weeks',
  'mkt.s6.metaScopeLabel': 'Starting scope',
  'mkt.s6.metaScopeValue': '1 class · 1 module',
  'mkt.s6.formTitle': 'Pilot inquiry',
  'mkt.s6.formHint': 'We respond within 2 business days.',
  'mkt.s6.form.org': 'Institution',
  'mkt.s6.form.orgPlaceholder':
    'e.g. Hanbit Middle School / Gangnam Math Hagwon',
  'mkt.s6.form.name': 'Contact name',
  'mkt.s6.form.namePlaceholder': 'Park Ji-won',
  'mkt.s6.form.role': 'Role',
  'mkt.s6.form.rolePlaceholder': 'Vice Principal / Director',
  'mkt.s6.form.email': 'Email',
  'mkt.s6.form.emailPlaceholder': 'name@school.kr',
  'mkt.s6.form.phone': 'Phone',
  'mkt.s6.form.phoneOptional': '(optional)',
  'mkt.s6.form.phonePlaceholder': '010-0000-0000',
  'mkt.s6.form.msg': 'Brief message',
  'mkt.s6.form.msgPlaceholder':
    'Grade level, subject, when you\'d like to start, anything that helps.',
  'mkt.s6.form.submit': 'Request a pilot',
  'mkt.s6.form.submitting': 'Sending…',
  'mkt.s6.form.errorGeneric':
    'Submission failed. Please try again or email contact@eehae.app directly.',
  'mkt.s6.form.successTitle': 'Inquiry received.',
  'mkt.s6.form.successBody':
    'A founder will respond within 1-2 business days. A confirmation email is on its way to the address you provided.',
  // PIPA Art. 15 §2 inline consent disclosure (issue #181). Kept
  // concise here; full §2 collection items + retention windows live
  // in the privacy policy linked below.
  'mkt.s6.form.consent':
    'By submitting, you consent to your information being collected for the purpose of responding to this pilot inquiry and retained for 1 year after the inquiry is closed. For details, see the',
  'mkt.s6.form.consentLink': 'privacy policy.',
  // Pre-#181 placeholder CTA (now removed; form is live). Keys
  // retained in case the form has to be temporarily disabled again.
  'mkt.s6.contactCta': 'Get in touch',
  'mkt.s6.contactNote':
    'For now we accept inquiries by email. A formal request form is coming soon.',

  'mkt.s7.tagline': 'Education should be better',
  'mkt.s7.info.cpoLabel': 'Chief Privacy Officer',
  // TODO: previous CPO left the project. Fill in the new designee's
  // name (PIPA Art. 31) and re-enable the gated footer row in
  // MarketingExtension.tsx (OPERATOR-INFO-PENDING).
  'mkt.s7.info.cpoValue': 'TBD',
  'mkt.s7.info.companyLabel': 'Company',
  'mkt.s7.info.companyValue': 'eehae Co., Ltd.',
  'mkt.s7.info.bizLabel': 'Registration',
  'mkt.s7.info.bizValueTbd': 'In progress',
  'mkt.s7.info.addressLabel': 'Address',
  // TODO: registered office address removed after the previous
  // operator's departure. Fill in the new registered address and
  // re-enable the gated footer row in MarketingExtension.tsx
  // (OPERATOR-INFO-PENDING).
  'mkt.s7.info.addressValue': 'TBD',
  'mkt.s7.info.contactLabel': 'Contact',
  'mkt.s7.info.contactValue': 'contact@eehae.app',
  'mkt.s7.col.product': 'Product',
  'mkt.s7.col.legal': 'Legal',
  'mkt.s7.col.contact': 'Contact',
  'mkt.s7.link.problem': 'Why',
  'mkt.s7.link.audiences': 'Who it\'s for',
  'mkt.s7.link.trust': 'Data & trust',
  'mkt.s7.link.how': 'How it works',
  'mkt.s7.link.terms': 'Terms',
  'mkt.s7.link.privacy': 'Privacy policy',
  'mkt.s7.link.cpo': 'CPO',
  'mkt.s7.link.cookies': 'Cookies',
  'mkt.s7.link.pilot': 'Request a pilot',
  'mkt.s7.link.press': 'Press · partnerships',
  'mkt.s7.copyright': '© 2026 eehae. All rights reserved.',

  // ── Account settings (PIPA P3, issue #129 PR 5) ──────────────
  //
  // Student-facing English mirrors the 5th-grade Korean — short
  // sentences, no jargon, "30-day backup window" rather than
  // "backup-rotation cascade window."
  'modal.cancel': 'Cancel',
  'account.settings.title': 'Account settings',
  'account.nav.back': 'Back',
  'account.educator.org': 'Organization: {name}',

  // Export section.
  'account.export.title': 'Download my data',
  'account.export.loading': 'Preparing…',
  'account.export.button.student': 'Get my data',
  'account.export.button.educator': 'Export my data',
  'account.export.student.desc':
    "You can download all your learning records as a zip file. Here's what's inside:",
  'account.export.includes.student.sessions':
    'Your sessions (questions, answers, teacher feedback)',
  'account.export.includes.student.assessments':
    "Your progress (which concepts you've mastered)",
  'account.export.includes.student.consents':
    'The terms you agreed to',
  'account.export.includes.student.accessLog':
    'Records of who looked at your information (teachers, admins)',
  'account.export.teacher.desc':
    'Download a zip of your own account data. School-owned class + student data is separate — your export includes:',
  'account.export.admin.desc':
    'Download a zip of your admin account data. The school billing fields are included — your export covers:',
  'account.export.includes.teacher.account':
    'Teacher account row + the classes / modules you manage',
  'account.export.includes.admin.account':
    'Admin account row + school info',
  'account.export.includes.educator.consents': 'Consent records',
  'account.export.includes.educator.accessLog':
    'Audit log of student data you accessed',
  'account.export.includes.admin.billing':
    'School subscription + billing fields (admin only)',

  // Delete section.
  'account.delete.title': 'Delete account',
  'account.delete.button.student': 'Delete account',
  'account.delete.button.educator': 'Delete account',
  'account.delete.student.desc':
    'Deleting your account also deletes your learning records. The data is kept safely for 30 days, then permanently removed.',
  'account.delete.teacher.desc':
    'Only your teacher account is deleted. School-owned classes, students, and sessions stay so the school can keep managing them.',
  'account.delete.admin.desc':
    'Only your admin account is deleted. School data and other teacher accounts remain. If you are the last admin in the school, promote another teacher to admin in Clerk first (otherwise the delete is rejected with 409).',

  // Confirmation modal.
  'account.delete.confirm.title.student': 'Delete your account?',
  'account.delete.confirm.title.teacher': 'Delete your teacher account?',
  'account.delete.confirm.title.admin': 'Delete your admin account?',
  'account.delete.confirm.button.student': 'Yes, delete',
  'account.delete.confirm.button.educator': 'Delete',
  'account.delete.confirm.loading': 'Working…',
  'account.delete.confirm.body.student.intro':
    "Here's what happens when you click delete:",
  'account.delete.confirm.body.student.tokens':
    "You're signed out right away. You'll have to log in again next time.",
  'account.delete.confirm.body.student.cascade':
    'Your records are kept for {days} days, then permanently deleted. (If you change your mind within {days} days, tell your teacher.)',
  'account.delete.confirm.body.teacher.intro':
    'When you continue:',
  'account.delete.confirm.body.admin.intro':
    'When you continue:',
  // Role-neutral "your account row" because this string is shared
  // between teacher AND admin variants. The earlier "teacher row"
  // phrasing was inaccurate when the caller was an admin (admins
  // are stored on the Teacher table, but admin-facing prose
  // shouldn't say "teacher"). CodeRabbit on PR #351.
  'account.delete.confirm.body.educator.cascade.row':
    'Only your account row is soft-deleted (deleted_at stamped).',
  'account.delete.confirm.body.educator.cascade.classes':
    'Your classes, students, and sessions stay; another admin can reassign them.',
  'account.delete.confirm.body.educator.cascade.window':
    'Permanently deleted after {days} days. Until then it stays as part of the backup rotation.',
  'account.delete.confirm.body.admin.lastAdmin':
    'If you are the only admin, the request returns 409. Promote another admin in Clerk first.',

  // Post-delete receipt screen.
  'account.delete.done.title': 'Account deletion received',
  'account.delete.done.body':
    'Your account is soft-deleted. It will be permanently removed after {days} days.',
  'account.delete.done.signedOut':
    "You're already signed out. Any next page will require a new login.",
  'account.delete.done.returnHome': 'Back to home',
  'account.delete.done.educator.teacher.followup':
    'Your classes can be reassigned to another teacher by a school admin.',
  'account.delete.done.educator.admin.followup':
    'School data you managed will continue to be managed by another admin.',
  'account.delete.done.educator.returnDashboard': 'Back to dashboard',

  // Settings link surfaced from each role-home page.
  'account.link.fromStudentLogin': 'Settings',
  'account.link.fromTeacherDashboard': 'Account settings',
  'account.link.fromAdminHome': 'Account settings',

  // ── Revocation banner (#306 surface 5) ─────────────────────
  'revocation.banner.title': 'Your access has changed',
  'revocation.banner.body':
    "An administrator may have changed your access. Please reload the page to see the latest state.",
  'revocation.banner.reload': 'Reload',
  'revocation.banner.dismiss': 'Dismiss',

  // ── ClassMaterialsPage (#451 PR-4) ─────────────────────────
  'materials.title': 'Class {code} — materials',
  'materials.subtitle':
    'Upload a textbook or unit PDF once; chapters are detected automatically so you can teach them day by day.',
  'materials.backToDashboard': '← Back to dashboard',
  'materials.backToClasses': '← Back to classes',
  'materials.missingClassCode': 'Invalid URL — missing class code.',
  'materials.loading': 'Loading materials...',
  'materials.empty.title': 'No materials uploaded yet',
  'materials.empty.body':
    'Upload your first textbook or unit PDF to get started — a whole-book upload is the recommended pattern.',
  'materials.error.title': 'Could not load materials',
  'materials.error.body':
    'This may be a transient network issue. Please try again in a moment.',

  // ── MaterialUpload ─────────────────────────────────────────
  'materials.upload.title': 'Upload new material',
  'materials.upload.replaceTitle': 'Replace material',
  'materials.upload.hint':
    'Max 100 MB. Whole-textbook uploads work best.',
  'materials.upload.filenameLabel': 'Display name',
  'materials.upload.filenamePlaceholder': 'e.g. MS Science Grade 7',
  'materials.upload.fileLabel': 'PDF file',
  'materials.upload.filenameRequired': 'Please enter a display name.',
  'materials.upload.fileRequired': 'Please select a PDF file.',
  'materials.upload.tooLarge': 'File too large (max 100 MB).',
  'materials.upload.submit': 'Upload',
  'materials.upload.submitReplace': 'Upload replacement',
  'materials.upload.uploading': 'Uploading...',
  'materials.upload.uploadFailed': 'Upload failed',
  'materials.upload.cancel': 'Cancel',

  // ── MaterialCard ───────────────────────────────────────────
  'materials.card.pages': '{count} pages',
  'materials.card.uploaded': 'Uploaded {date}',
  'materials.card.chunked': 'Chapters detected',
  'materials.card.chunkPending': 'Detecting chapters...',
  'materials.card.teachChapter': 'Teach a chapter',
  'materials.card.replace': 'Replace',

  // ── ChapterPicker ──────────────────────────────────────────
  'chapter.picker.title': 'Pick a chapter',
  'chapter.picker.titleWithFile': '{filename} — pick a chapter',
  'chapter.picker.close': 'Close',
  'chapter.picker.loading': 'Loading chapter index...',
  'chapter.picker.loadFailed': 'Could not load chapter index.',
  'chapter.picker.pageRange': 'pp. {start}–{end}',
  'chapter.picker.teach': 'Teach this chapter',
  'chapter.picker.teaching': 'Creating...',
  'chapter.picker.buildFailed': 'Lesson creation failed.',
  'chapter.picker.detectionPending':
    'Chapter detection is still running. Try again in a moment, or use the manual form below.',
  'chapter.picker.detectionEmpty':
    "We couldn't auto-detect chapters in this PDF. Enter a manual page range below.",
  'chapter.picker.manualTitle': 'Manual page range',
  'chapter.picker.manualHint':
    'Pick your own chapter name + page range to build a lesson.',
  'chapter.picker.manualNameLabel': 'Lesson name',
  'chapter.picker.manualNameRequired': 'Please enter a lesson name.',
  'chapter.picker.manualPagesInvalid':
    'Enter valid start + end pages (start ≤ end).',
  'chapter.picker.manualStart': 'Start',
  'chapter.picker.manualEnd': 'End',

  // ── TeacherDashboard (#451 PR-4 additions, #515 skeleton) ─
  'teacher.dashboard.manageMaterials': 'Manage materials',
  'teacher.dashboard.loading': 'Loading dashboard...',

  // ── TodaysFocusPill ────────────────────────────────────────
  'focus.current': "Today's lesson: {name}",
  'focus.none': "No lesson set for today",
  'focus.setCta': 'Set',
  'focus.clear': 'Clear',
  'focus.clearing': 'Clearing...',
  'focus.loadFailed': "Couldn't load focus state",
  'focus.staleFallback': "Today's lesson info is out of date",

  // ── Help / documentation surface (#620, PR 1 — scaffold) ───
  'help.brand.suffix': 'Help',
  'help.sidebar.aria': 'Help categories',
  'help.sidebar.tag.teacher': 'Teacher',
  'help.sidebar.tag.student': 'Student',
  'help.sidebar.tag.admin': 'Admin',
  'help.sidebar.all_topics': 'All topics',
  'help.sidebar.search.placeholder': 'Search docs',
  'help.sidebar.search.aria': 'Search help',
  'help.topbar.search.placeholder': 'Search help…',
  'help.topbar.search.aria': 'Search help',
  'help.topbar.breadcrumb.aria': 'You are here',
  'help.hero.teacher.eyebrow': 'TEACHER GUIDE',
  'help.hero.teacher.title': 'Run your class with confidence',
  'help.hero.teacher.lede':
    'From school setup to module builds to reading student insight — the flows you ask about most, in one place.',
  'help.hero.student.eyebrow': 'STUDENT GUIDE',
  'help.hero.student.title': "Today's lesson, made easier",
  'help.hero.student.lede':
    'How to sign in, talk with your tutor, and review your notes — the basics for keeping your learning flow going.',
  'help.hero.admin.eyebrow': 'ADMIN GUIDE',
  'help.hero.admin.title': 'School operations & compliance',
  'help.hero.admin.lede':
    'Seats, billing, PIPA compliance, and the audit log — what school operators reach for most.',
  'help.hero.search.placeholder': 'What can we help with?',
  'help.hero.search.aria': 'Search help',
  'help.hero.meta.articleCount': '{count} articles',
  'help.hero.meta.docsVersion': 'Version {version}',
  'help.landing.popular.title': 'Most-read',
  'help.landing.popular.meta': '{count} featured',
  'help.landing.categories.title': 'Browse by category',
  'help.landing.categories.meta': '{count} categories',
  'help.card.readingTime': '{min} min read',
  'help.coming_soon.title': 'Coming soon',
  'help.coming_soon.body': 'This article will be published soon.',
  'help.coming_soon.note':
    "We're still writing this one up. If you need help in the meantime, please reach out to your school admin or the teacher who set up your class.",
  'help.coming_soon.back': 'Back to help',
  'help.entry.label': 'Help',
  'help.entry.aria': 'Go to Help',
  // Search results (PR 3 additions).
  'help.search.results.title': 'Search results',
  'help.search.results.meta': '{count} articles',
  'help.search.noMatches.title': 'No matching articles',
  'help.search.noMatches.meta': '"{q}"',
  'help.search.noMatches.hint':
    'Try different keywords, or browse by category below.',
  // Article page (PR 2 additions).
  'help.toc.label': 'On this page',
  // Help → back to the regular app view (sidebar exit link).
  'help.back.teacher': 'Back to dashboard',
  'help.back.student': 'Back to lessons',
  'help.back.admin': 'Back to admin',
  'help.article.updatedAt': 'Last updated {date}',
  'help.article.version': 'Doc version {v}',
  'help.article.reviewedBy': 'Reviewed by {who}',
  'help.article.related': 'Related article',
  // Admin contact form + escalation (PR 4 additions).
  'help.contact.sidebar.label': 'Contact eehae team',
  'help.contact.crumb': 'Contact eehae team',
  'help.contact.title': 'Contact eehae operations',
  'help.contact.subtitle':
    "For school operations, billing, compliance, and technical-integration questions. Your message plus send time, user ID, school ID, and policy version are written to the audit log automatically.",
  'help.contact.fields.category': 'Category',
  'help.contact.fields.subject': 'Subject',
  'help.contact.fields.subject.placeholder': 'e.g. Duplicate May invoice',
  'help.contact.fields.message': 'Message',
  'help.contact.fields.message.hint':
    "Include the situation and which screen / student / class is affected. Don't include student personal data directly — use an anonymized ID instead.",
  'help.contact.fields.message.placeholder':
    'e.g. We received two May invoices, both already paid. Invoice IDs INV-2026-05-0421 / 0422. Please confirm refund eligibility.',
  'help.contact.category.billing.label': 'Billing',
  'help.contact.category.billing.hint': 'Invoices, payment, refunds, tax',
  'help.contact.category.compliance.label': 'Compliance',
  'help.contact.category.compliance.hint': 'PIPA, DPA, data-subject responses',
  'help.contact.category.technical.label': 'Technical',
  'help.contact.category.technical.hint': 'Clerk, webhooks, SSO, integrations',
  'help.contact.category.other.label': 'Other',
  'help.contact.category.other.hint': 'Anything else',
  'help.contact.audit.label': 'Audit-log entry',
  'help.contact.audit.body':
    "This request is written to the audit log together with the message, send time, user ID, school ID, policy version, and category. Don't include student personal data directly — use an anonymized ID instead.",
  'help.contact.actions.send': 'Send message',
  'help.contact.actions.sending': 'Sending...',
  'help.contact.actions.reset': 'Reset',
  'help.contact.charCount': '{n} / {max}',
  'help.contact.error.label': 'Send failed',
  'help.contact.success.title': 'Message received',
  'help.contact.success.body':
    "You'll hear back within 1 business day. Reference number #{id} — please cite if you follow up.",
  'help.contact.success.another': 'Send another',
  'help.contact.aside.flow.title': 'How a request flows',
  'help.contact.aside.flow.step1': 'Routes to the matching operations queue.',
  'help.contact.aside.flow.step2': 'Message + metadata is written to the audit log.',
  'help.contact.aside.flow.step3': 'Owner responds within 1 business day.',
  'help.contact.aside.sla.window': 'Reply window',
  'help.contact.aside.sla.window.value': '1 business day',
  'help.contact.aside.sla.compliance': 'Compliance issues',
  'help.contact.aside.sla.compliance.value': 'Within 4 hours',
  'help.contact.aside.sla.hours': 'Hours',
  'help.contact.aside.sla.hours.value': '09:00 – 19:00 KST',
  'help.contact.aside.sla.email': 'Email',
  'help.contact.aside.sla.email.value': 'contact@eehae.app',
  // Escalation panel for teacher + student landings.
  'help.escalation.title': "Couldn't find what you needed?",
  'help.escalation.teacher.body':
    "Operational and policy questions that the dashboard can't resolve go to your school admin. Your admin has a dedicated page for contacting the eehae team directly.",
  'help.escalation.student.body':
    "If the help center didn't answer it, send your teacher a quick note. They know your class the best and you'll usually get a reply before the next lesson.",
}

export default en
